import { get, join, memoize } from 'lodash';
import React from 'react';
import { PARTNER_ACCOUNT_PATH } from '@atlassian/cssxp-typescommon';
import { PartnerAccountSearchParams } from '@atlassian/cssxp-types-customer';
// @ts-ignore
import AvatarIcon from '../../assets/img/icon-avatar.svg';
import {
  ColGroup,
  SearchResultColumn,
  SearchResultsTable,
  TableBody,
  TableHead,
  PAGE_TAB_SEARCH_RESULTS_LOAD_MORE,
} from '../..';
import { useQuery } from '@apollo/client';
import { fetchPartnerAccounts } from './queries/fetch-partner-accounts';
import {
  FetchPartnerAccountsQuery,
  FetchPartnerAccountsQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';

type Props = {
  params: PartnerAccountSearchParams;
  shouldIncludeRole?: boolean;
  redirectBaseURL?: string;
};

const getColumns = memoize((shouldIncludeRole = false): SearchResultColumn[] => {
  const columns = [
    {
      name: 'Partner accounts',
      customClassNames: 'col-40',
    },
  ];

  if (shouldIncludeRole) {
    columns.push({
      name: 'Role',
      customClassNames: 'col-60',
    });
  } else {
    columns.push(
      {
        name: 'Domain',
        customClassNames: 'col-40',
      },
      {
        name: 'Partner level',
        customClassNames: 'col-20',
      }
    );
  }
  // Add the empty column at the end
  // @ts-ignore
  columns.push({ name: ' ' });
  return columns;
});

export const defaultSearchOption = {
  size: PAGE_TAB_SEARCH_RESULTS_LOAD_MORE,
  from: 0,
};

export const PartnerAccountSearch = ({ params, shouldIncludeRole = false }: Props) => {
  const { data, error, loading, fetchMore } = useQuery<FetchPartnerAccountsQuery, FetchPartnerAccountsQueryVariables>(
    fetchPartnerAccounts,
    {
      variables: {
        ...params,
        searchOptions: defaultSearchOption,
      },
      displayName: 'fetchPartnerAccount',
      notifyOnNetworkStatusChange: true,
    }
  );

  const handleLoadMoreClick = () => {
    defaultSearchOption.size = defaultSearchOption.size + PAGE_TAB_SEARCH_RESULTS_LOAD_MORE;
    fetchMore({
      query: fetchPartnerAccounts,
      variables: {
        ...params,
        searchOptions: defaultSearchOption,
      },
      updateQuery: (
        previousResult: FetchPartnerAccountsQuery,
        { fetchMoreResult }: { fetchMoreResult?: FetchPartnerAccountsQuery }
      ) => {
        if (fetchMoreResult?.fetchPartnerAccounts?.results && previousResult?.fetchPartnerAccounts?.results) {
          fetchMoreResult.fetchPartnerAccounts.results = [
            ...previousResult.fetchPartnerAccounts.results,
            ...fetchMoreResult.fetchPartnerAccounts.results,
          ];
        }
        // added a fallback in case fetchMore result is empty
        return fetchMoreResult || previousResult;
      },
    });
  };

  const partnerAccounts = data?.fetchPartnerAccounts?.results;
  const totalAccounts = data?.fetchPartnerAccounts?.totalHits ?? 0;
  const showLoadMore = totalAccounts && partnerAccounts ? totalAccounts > (partnerAccounts ?? []).length : false;
  const hasNoResults = (partnerAccounts ?? []).length === 0;

  return (
    <div id="partnerAccounts" data-testid="partnerAccounts" style={{ order: hasNoResults ? 1 : 0 }}>
      <SearchResultsTable
        id="partnerAccounts"
        isLoading={loading}
        error={error?.toString()}
        heading={'Partner Accounts'}
        noResults={hasNoResults}
        loadMore={{
          showLoadMore,
          totalCount: totalAccounts,
          resultsCount: partnerAccounts?.length,
          onLoadMoreClick: handleLoadMoreClick,
        }}
        entityName="partner accounts"
      >
        <ColGroup columns={getColumns(shouldIncludeRole)} />
        <TableHead columns={getColumns(shouldIncludeRole)} />
        <TableBody>
          {partnerAccounts?.map((partner, index: number) => {
            if (!partner) {
              return;
            }
            const URL = `${PARTNER_ACCOUNT_PATH}/${encodeURIComponent(partner.id as string)}`;
            return (
              <tr key={partner.id} data-testid={`partner-account-table-${index}`}>
                <td>
                  <a href={URL} target="_blank">
                    {/* @ts-ignore */}
                    <img src={AvatarIcon} className="avatar medium" />
                    {partner.name}
                  </a>
                </td>
                {!shouldIncludeRole && (
                  <>
                    <td>{partner.domains && partner.domains.main}</td>
                    <td>{partner.programLevelGlobal}</td>
                  </>
                )}
                {shouldIncludeRole && <td>{join(get(partner, 'contactAccountRole.roles', ''), ', ')}</td>}
              </tr>
            );
          })}
        </TableBody>
      </SearchResultsTable>
    </div>
  );
};
